import { useState, type ChangeEvent, type FormEvent } from 'react';
import { GADGET_CATEGORIES } from '@gc/domain';
import type { ApiClient, CreateGadgetInput, GadgetRow, UpdateGadgetInput } from '@gc/api-client';
import { useAction } from '@gc/ui-state';
import { words } from './labels.js';

export type GadgetFormApi = { gadgets: Pick<ApiClient['gadgets'], 'create' | 'update'> };
export type GadgetFormMode = { kind: 'create' } | { kind: 'edit'; gadget: GadgetRow };

type Field =
  | 'assetTag' | 'serial' | 'imei' | 'make' | 'model' | 'category' | 'purchasedOn' | 'price'
  | 'supplier' | 'warrantyEnds' | 'guaranteeEnds' | 'ownerTeam' | 'location' | 'notes';
type Values = Record<Field, string>;

const day = (v: string | null | undefined): string => (v ? v.slice(0, 10) : '');

const initial = (g: GadgetRow | null): Values => ({
  assetTag: g?.assetTag ?? '', serial: g?.serial ?? '', imei: g?.imei ?? '', make: g?.make ?? '',
  model: g?.model ?? '', category: g?.category ?? 'laptop', purchasedOn: day(g?.purchasedOn),
  price: g?.priceMinor === null || g?.priceMinor === undefined ? '' : String(g.priceMinor / 100),
  supplier: g?.supplier ?? '', warrantyEnds: day(g?.warrantyEnds), guaranteeEnds: day(g?.guaranteeEnds),
  ownerTeam: g?.ownerTeam ?? '', location: g?.location ?? '', notes: g?.notes ?? '',
});

const recordFields = (v: Values): Record<string, string | number> => {
  const out: Record<string, string | number> = {};
  for (const k of ['assetTag', 'serial', 'imei', 'make', 'model', 'category', 'purchasedOn', 'supplier',
    'warrantyEnds', 'guaranteeEnds', 'notes'] as const) {
    if (v[k].trim() !== '') out[k] = v[k].trim();
  }
  if (v.price.trim() !== '') out['priceMinor'] = Math.round(Number(v.price) * 100);
  return out;
};

export function GadgetForm({ api, mode, onDone, onCancel }: {
  api: GadgetFormApi;
  mode: GadgetFormMode;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [v, setV] = useState<Values>(() => initial(mode.kind === 'edit' ? mode.gadget : null));
  const { busy, failure, run } = useAction();

  const set = (k: Field) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setV((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      if (mode.kind === 'create') {
        await api.gadgets.create({
          ...recordFields(v),
          ownerTeam: v.ownerTeam.trim(),
          status: 'procured',
          ...(v.location.trim() ? { location: v.location.trim() } : {}),
        } as CreateGadgetInput);
        return;
      }
      const before = recordFields(initial(mode.gadget));
      const patch = Object.fromEntries(Object.entries(recordFields(v)).filter(([k, x]) => before[k] !== x));
      if (Object.keys(patch).length > 0) await api.gadgets.update(mode.gadget.id, patch as UpdateGadgetInput);
    }).then((ok) => { if (ok) onDone(); });
  };

  return (
    <form className="form" onSubmit={submit} data-testid="gadget-form">
      <h3>{mode.kind === 'create' ? 'Add a gadget' : `Edit ${mode.gadget.shortId}`}</h3>
      {failure && <p role="alert" data-testid="gadget-form-failure">{failure}</p>}
      <div className="grid">
        <label>Asset tag<input required value={v.assetTag} onChange={set('assetTag')} data-testid="field-assetTag" /></label>
        <label>Make<input required value={v.make} onChange={set('make')} data-testid="field-make" /></label>
        <label>Model<input required value={v.model} onChange={set('model')} data-testid="field-model" /></label>
        <label>Category
          <select value={v.category} onChange={set('category')} data-testid="field-category">
            {GADGET_CATEGORIES.map((c) => <option key={c} value={c}>{words(c)}</option>)}
          </select>
        </label>
        <label>Serial<input value={v.serial} onChange={set('serial')} /></label>
        <label>IMEI<input inputMode="numeric" value={v.imei} onChange={set('imei')} /></label>
        <label>Purchased on<input type="date" value={v.purchasedOn} onChange={set('purchasedOn')} /></label>
        <label>Price (₹)<input type="number" min="0" step="0.01" value={v.price} onChange={set('price')} data-testid="field-price" /></label>
        <label>Supplier<input value={v.supplier} onChange={set('supplier')} /></label>
        <label>Warranty ends<input type="date" value={v.warrantyEnds} onChange={set('warrantyEnds')} /></label>
        <label>Guarantee ends<input type="date" value={v.guaranteeEnds} onChange={set('guaranteeEnds')} /></label>
        {mode.kind === 'create' && (
          <>
            <label>Owning team<input required value={v.ownerTeam} onChange={set('ownerTeam')} data-testid="field-ownerTeam" /></label>
            <label>Location<input value={v.location} onChange={set('location')} /></label>
          </>
        )}
      </div>
      <label>Notes<textarea rows={3} value={v.notes} onChange={set('notes')} /></label>
      <div className="form-actions">
        <button type="submit" className="primary" disabled={busy} data-testid="gadget-form-save">{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}
